/**
 * Restores (and persists) the scroll position of a doc open in the right detail
 * pane, per canvas — so a tab switch returns you to where you were reading.
 *
 * No-op outside a canvas route, without a content id, or when there's no Radix
 * `ScrollArea` viewport in `containerRef` (full-bleed panes scroll inside an
 * iframe — deferred). scrollTop is applied once `ready` AND the content is tall
 * enough to honor the saved offset (watched via rAF), so it doesn't snap early.
 */

import { ROUTES } from "@/constants/routes";
import { useEffect, useRef } from "react";
import { useMatch } from "react-router-dom";
import {
  readCanvasSessionScroll,
  writeCanvasSessionScroll,
} from "./canvasSessionStorage";

const MAX_RESTORE_FRAMES = 60; // ~1s at 60fps before giving up and best-effort.
const CAPTURE_DEBOUNCE_MS = 200;

export function useRightPaneScrollRestore(
  containerRef: React.RefObject<HTMLElement | null>,
  contentId: string | null | undefined,
  /** True once the doc content has loaded (so its height is meaningful). */
  ready: boolean,
) {
  const canvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const canvasId = canvasMatch?.params?.canvasId ?? null;

  const restoredRef = useRef(false);
  useEffect(() => {
    restoredRef.current = false;
  }, [canvasId, contentId]);

  // Restore once content is ready and the saved offset is reachable.
  useEffect(() => {
    if (!canvasId || !contentId || !ready || restoredRef.current) return;

    const target = readCanvasSessionScroll(canvasId, contentId);
    if (target == null || target <= 0) {
      restoredRef.current = true;
      return;
    }

    // Poll by frame: the viewport may mount a tick after `ready`, and the
    // content keeps growing until layout settles. Apply once it's reachable, or
    // give up after ~1s (best-effort).
    let raf = 0;
    let frames = 0;
    const apply = () => {
      frames += 1;
      const viewport = containerRef.current?.querySelector<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      );
      const reachable =
        !!viewport && viewport.scrollHeight - viewport.clientHeight >= target;
      if (viewport && (reachable || frames >= MAX_RESTORE_FRAMES)) {
        viewport.scrollTop = target;
        restoredRef.current = true;
        return;
      }
      if (frames >= MAX_RESTORE_FRAMES) {
        restoredRef.current = true; // no viewport (full-bleed) — stop trying
        return;
      }
      raf = requestAnimationFrame(apply);
    };
    raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [canvasId, contentId, ready, containerRef]);

  // Capture scroll (debounced) once the restore has settled, so we never persist
  // the pre-restore scrollTop of 0.
  useEffect(() => {
    if (!canvasId || !contentId) return;
    const viewport = containerRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (!restoredRef.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        writeCanvasSessionScroll(canvasId, contentId, viewport.scrollTop);
      }, CAPTURE_DEBOUNCE_MS);
    };
    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      viewport.removeEventListener("scroll", onScroll);
    };
  }, [canvasId, contentId, ready, containerRef]);
}
